"use client";

import * as React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────────
 * DividendHandlingSelect — pill-based segmented control.
 *
 * Two options surface as pills:
 *   • DRIP — active (dividends reinvest into more units at the ex-div day's
 *     close; existing simulator math).
 *   • Cash — disabled, "Coming soon" tooltip. When wired in a future slice,
 *     the simulator will pay dividends into an external cash stream surfaced
 *     in the breakdown table and net-wealth formula.
 *
 * Same visual register as LendingTypeSelect — segmented buttons with a
 * subtle active fill and tooltip-on-hover for disabled options.
 * ────────────────────────────────────────────────────────────────────────── */

export type DividendHandling = "drip" | "cash";

interface DividendOption {
  value: DividendHandling;
  label: string;
  available: boolean;
}

export const DIVIDEND_HANDLINGS: readonly DividendOption[] = [
  { value: "drip", label: "DRIP", available: true },
  { value: "cash", label: "Cash", available: false },
] as const;

export interface DividendHandlingSelectProps {
  value: DividendHandling;
  onChange: (value: DividendHandling) => void;
  disabled?: boolean;
}

export function DividendHandlingSelect({ value, onChange, disabled }: DividendHandlingSelectProps) {
  return (
    <TooltipProvider delayDuration={150}>
      <div
        role="group"
        aria-label="Dividend handling"
        className="border-input bg-background flex h-9 gap-1 rounded-md border p-0.5"
      >
        {DIVIDEND_HANDLINGS.map((opt) => {
          const active = value === opt.value;
          const interactive = opt.available && !disabled;
          const button = (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              aria-disabled={!interactive}
              disabled={!interactive}
              onClick={() => {
                if (interactive) onChange(opt.value);
              }}
              className={cn(
                "flex-1 rounded-sm px-2 text-xs font-medium tracking-tight transition-colors",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                active && interactive
                  ? "bg-stone-200/80 text-zinc-900 shadow-sm"
                  : interactive
                    ? "text-zinc-600 hover:bg-stone-100 hover:text-zinc-900"
                    : "text-zinc-400 disabled:pointer-events-auto",
              )}
            >
              {opt.label}
            </button>
          );

          if (opt.available) return button;
          return (
            <Tooltip key={opt.value}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>Coming soon</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
